import React from 'react'
import { Link } from 'react-router-dom'
import SuperUserHomePageUI from '../../../templates/SuperUserHomePageUI'
import SpinnerComponentUI from '../../../components/common/SpinnerComponentUI'
import SpinnerUI from '../../../widgets/output/SpinnerUI'
import IconButtonUI from '../../../widgets/action/IconButtonUI'
import LRTableActionsUI from '../../../widgets/layout/LRTableActionsUI'
import SearchUI from '../../../widgets/input/SearchUI'
import SelectUI from '../../../widgets/input/SelectUI'
import CardUI from '../../../widgets/layout/CardUI'
import BadgeUI from '../../../widgets/output/BadgeUI'
import EmptyListUI from '../../../widgets/output/EmptyListUI'
import ButtonTinyUI from '../../../widgets/action/ButtonTinyUI'
import ModalFormUI from '../../../widgets/form/ModalFormUI'
import Currency from '../../../components/common/Currency'
import RepeatTransactionActionModalSF from './RepeatTransactionActionModalSF'
import './RepeatTransactionPage.css'

const RepeatTransactionPageUI = ({
  context,
  errorList,
  eligibilityList,
  loading,
  loadingPage,
  currentPage,
  totalPages,
  totalOffers,
  hasMore,
  offerDate,
  isCheckingEligibility,
  eligibilityConfirmOpen,
  onRunEligibilityCheckClick,
  onEligibilityConfirmModalRequestClose,
  onRunEligibilityCheckConfirm,
  onRunEligibilityCheck,
  onRefreshClick,
  onPreviousPage,
  onNextPage,
  searchQuery,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  actionModalOpen,
  selectedItem,
  actionModalType,
  onOpenActionModal,
  onCloseActionModal,
  onActionSuccess
}) => {
  const isNetworkOrBackendError = (err) => {
    if (!err) return false
    const str = typeof err === 'string'
      ? err
      : (err.message || err.errorMessage || err.error || err.description || JSON.stringify(err) || '')
    const lower = str.toLowerCase()
    const code = (typeof err === 'object' && err !== null) ? String(err.errorCode || err.code || err.status || err.statusCode || '') : ''

    return (
      lower.includes('network error') ||
      lower.includes('networkerror') ||
      lower.includes('network_error') ||
      lower.includes('http error') ||
      lower.includes('network/cors') ||
      lower.includes('network/parse') ||
      lower.includes('failed to fetch') ||
      lower.includes('failed to load') ||
      lower.includes('econnrefused') ||
      lower.includes('etimedout') ||
      lower.includes('internal server error') ||
      lower.includes('backend error') ||
      lower.includes('could not fetch') ||
      lower.includes('status: 5') ||
      lower.includes('status code 5') ||
      lower.includes('500') ||
      lower.includes('502') ||
      lower.includes('503') ||
      lower.includes('504') ||
      code === '500' || code === '502' || code === '503' || code === '504' ||
      (typeof err === 'object' && err !== null && (err.status >= 500 || err.statusCode >= 500))
    )
  }

  const isResourceNotFoundError = (err) => {
    if (!err) return false
    const str = typeof err === 'string' ? err : JSON.stringify(err)
    return str.includes('ResourceNotFoundException') || str.includes('Requested resource not found')
  }

  const getErrorMessage = (err) => {
    if (!err) return ''
    if (typeof err === 'string') return err
    if (typeof err === 'object') {
      const msg = err.message || err.errorMessage || err.error || err.description
      const code = err.errorCode || err.code
      const objCode = err.objectCode
      if (code && msg) return `[${code}] ${msg}`
      if (msg) return msg
      if (code) return `[${code}]`
      if (objCode) return `(${objCode})`
      return JSON.stringify(err)
    }
    return String(err)
  }

  const isNetworkError = Boolean(errorList && errorList.length > 0 && errorList.some(isNetworkOrBackendError))
  const otherErrors = (errorList || []).filter(err => !isNetworkOrBackendError(err) && !isResourceNotFoundError(err))

  const displayErrors = []
  if (isNetworkError) {
    displayErrors.push('Network Error: Could not fetch the data')
  }
  if (otherErrors.length > 0) {
    otherErrors.forEach(err => {
      const msg = getErrorMessage(err)
      if (msg) displayErrors.push(msg)
    })
  }

  const hasError = displayErrors.length > 0
  const isDataLoading = loading && !hasError && (eligibilityList === null || eligibilityList === undefined)
  const canRunEligibility = Boolean(
    context &&
    context.permissions &&
    context.permissions.includes('su_superuser_business')
  )

  // Robust field extractors supporting RepeatOfferDTO and legacy fields
  const getItemAccountId = (item) => {
    if (!item) return null
    return item.accountId ||
      (item.loanEntity && item.loanEntity.accountId) ||
      (item.account && item.account.accountId) ||
      null
  }

  const getItemAccountName = (item) => {
    if (!item) return '-'
    return item.accountName ||
      item.clientName ||
      (item.loanEntity && item.loanEntity.accountName) ||
      (item.account && (item.account.accountName || item.account.name)) ||
      '-'
  }

  const getItemOfferAmount = (item) => {
    if (!item) return 0
    if (item.offerAmountF) {
      const parsed = parseFloat(String(item.offerAmountF).replace(/[^0-9.-]+/g, ''))
      if (!isNaN(parsed)) return parsed
    }
    if (item.offerAmount != null) {
      const parsed = parseFloat(String(item.offerAmount).replace(/[^0-9.-]+/g, ''))
      if (!isNaN(parsed)) return parsed
    }
    if (item.preApprovedAmount != null) return parseFloat(item.preApprovedAmount) || 0
    if (item.eligibleAmount != null) return parseFloat(item.eligibleAmount) || 0
    return 0
  }

  const getNormalizedStatus = (item) => {
    if (!item) return 'Pending'
    const rawStatus = (item.offerStatus || item.statusOfLoan || item.status || '').toLowerCase()
    if (rawStatus.includes('reject')) return 'Rejected'
    if (rawStatus.includes('approve') || rawStatus.includes('pre_approved') || rawStatus.includes('term_sheet')) return 'Approved'
    return 'Pending'
  }

  const getItemUpdatedDate = (item) => {
    if (!item) return null
    if (item.dateUpdatedF1) return item.dateUpdatedF1
    const raw = item.dateUpdatedISO || item.updatedDateISO
    if (raw) {
      return typeof raw === 'string' && raw.includes('T') ? raw.replace('T', ' ').substring(0, 19) : String(raw)
    }
    const epoch = item.dateUpdated || item.updatedDate
    if (epoch) {
      try {
        return new Date(Number(epoch)).toLocaleString()
      } catch (e) {
        return String(epoch)
      }
    }
    return null
  }

  // Safe list
  const safeList = eligibilityList || []

  // Filtering list
  const filteredList = safeList.filter((item) => {
    const accountName = getItemAccountName(item).toLowerCase()
    const normStatus = getNormalizedStatus(item)
    const matchesSearch = !searchQuery || accountName.includes(searchQuery.toLowerCase())
    const matchesStatus = !statusFilter || statusFilter === 'ALL' ||
      ((statusFilter === 'APPROVED' || statusFilter === 'PRE_APPROVED') && normStatus === 'Approved') ||
      (statusFilter === 'REJECTED' && normStatus === 'Rejected') ||
      (statusFilter === 'PENDING' && normStatus === 'Pending')
    return matchesSearch && matchesStatus
  })

  // KPI Computations
  const totalAmount = safeList.reduce((acc, curr) => {
    const amt = getItemOfferAmount(curr)
    return acc + (isNaN(amt) ? 0 : amt)
  }, 0)

  const totalAccounts = safeList.length
  const rejectedCount = safeList.filter(item => getNormalizedStatus(item) === 'Rejected').length
  const approvedCount = safeList.filter(item => getNormalizedStatus(item) === 'Approved').length
  const pendingCount = totalAccounts - rejectedCount - approvedCount

  const statusOptions = [
    { value: 'ALL', label: 'All Statuses' },
    { value: 'PENDING', label: 'Pending' },
    { value: 'APPROVED', label: 'Approved' },
    { value: 'REJECTED', label: 'Rejected' }
  ]

  // Pagination & Run metadata
  const currPage = currentPage || 1
  const totalPageCount = totalPages != null ? totalPages : 1
  const isPreviousDisabled = Boolean(loadingPage || isCheckingEligibility || currPage >= totalPageCount || hasMore === false)
  const isNextDisabled = Boolean(loadingPage || isCheckingEligibility || currPage <= 1)

  const getRunDateTime = () => {
    const firstItem = safeList[0]
    if (!firstItem) {
      return offerDate || ''
    }

    // 1. If dateCreatedF1 already contains time (e.g., "03 Nov, 2026 12:40 PM")
    if (firstItem.dateCreatedF1 && (firstItem.dateCreatedF1.includes(':') || /am|pm/i.test(firstItem.dateCreatedF1))) {
      return firstItem.dateCreatedF1
    }

    // 2. Resolve a Date object with exact timestamp
    let dateObj = null

    // Prefer dateCreated if it contains numeric epoch millis or valid date string
    if (firstItem.dateCreated) {
      const num = Number(firstItem.dateCreated)
      if (!isNaN(num) && num > 0) {
        dateObj = new Date(num)
      } else {
        const parsed = new Date(firstItem.dateCreated)
        if (!isNaN(parsed.getTime())) dateObj = parsed
      }
    }

    // If dateCreated wasn't available, check dateCreatedISO
    if (!dateObj || isNaN(dateObj.getTime())) {
      if (firstItem.dateCreatedISO) {
        const parsed = new Date(firstItem.dateCreatedISO)
        if (!isNaN(parsed.getTime())) dateObj = parsed
      }
    }

    // Format date and time
    if (dateObj && !isNaN(dateObj.getTime())) {
      const timePart = dateObj.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })
      if (firstItem.dateCreatedF1) {
        return `${firstItem.dateCreatedF1}, ${timePart}`
      }
      return dateObj.toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      })
    }

    return firstItem.dateCreatedF1 || firstItem.dateCreatedISO || offerDate || ''
  }

  const runDate = getRunDateTime()

  return (
    <SuperUserHomePageUI
      context={context}
      className='pg_su_repeat_transaction'
      topnav='accounts'
      headerText='Repeat Transactions'
      centered={isDataLoading}
      page='repeatTransaction'
      dimension='w1600'
    >
      {isDataLoading && <SpinnerComponentUI />}

      {!isDataLoading && (
        <div>
          {/* Top Error Alert Bar */}
          {displayErrors.length > 0 && (
            <div className='pa_error_box'>
              {displayErrors.map((msg, index) => (
                <div key={index}>{msg}</div>
              ))}
            </div>
          )}

          {/* Action Header & Search Toolbar - Console Design Standard */}
          <LRTableActionsUI
            location='top'
            left={
              <div className='g_flex_h' style={{ alignItems: 'center' }}>
                <IconButtonUI icon='icon-refresh' onClick={onRefreshClick} />
                <span className='g_m_left_100 g_large'><b>Repeat Transactions</b></span>
              </div>
            }
            right={
              <div className='g_flex_h g_flex_right' style={{ alignItems: 'center' }}>
                <div style={{ minWidth: '160px' }}>
                  <SelectUI
                    selectedKey={statusFilter}
                    optionList={statusOptions}
                    onChange={(opt) => onStatusFilterChange(opt ? opt.value : 'ALL')}
                    placeholder='Status'
                  />
                </div>
                <div className='g_m_left_50'>
                  <SearchUI
                    value={searchQuery}
                    onChange={(e) => onSearchChange(e && e.target ? e.target.value : e)}
                    placeholder='Search account name...'
                  />
                </div>
                {canRunEligibility && (
                  <div className='g_m_left_50 g_flex_h' style={{ alignItems: 'center' }}>
                    <ButtonTinyUI
                      label='Run Eligibility Check'
                      className='pa_btn_run_eligibility'
                      onClick={onRunEligibilityCheckClick || onRunEligibilityCheck}
                      disabled={isCheckingEligibility}
                    />
                    {isCheckingEligibility && (
                      <SpinnerUI className='g_m_left_50' show={true} />
                    )}
                  </div>
                )}
              </div>
            }
          />

          {/* Running Eligibility Check Spinner */}
          {isCheckingEligibility ? (
            <div className='g_m_top_200' style={{ padding: '80px 0', textAlign: 'center' }}>
              <SpinnerComponentUI />
              <div className='g_m_top_100 g_small g_muted'>
                Running eligibility check and reloading latest offers...
              </div>
            </div>
          ) : (
            <div>
              {/* Top KPI Metrics Row using standard CardUI */}
              <div className='pa_kpi_grid g_m_top_100'>
                <CardUI>
                  <div className='g_flex_v'>
                    <span className='g_muted g_small'>Total Amount</span>
                    <span className='g_xxlarge g_col_green g_m_top_25'>
                      <b>{Currency.format(totalAmount)}</b>
                    </span>
                    <span className='g_tiny g_muted g_m_top_25'>Across all eligible repeat accounts</span>
                  </div>
                </CardUI>

                <CardUI>
                  <div className='g_flex_v'>
                    <span className='g_muted g_small'>Eligible Accounts</span>
                    <span className='g_xxlarge g_m_top_25'>
                      <b>{totalAccounts}</b>
                    </span>
                    <span className='g_tiny g_muted g_m_top_25'>Repeat loan candidates</span>
                  </div>
                </CardUI>

                <CardUI>
                  <div className='g_flex_v'>
                    <span className='g_muted g_small'>Approved</span>
                    <span className='g_xxlarge g_col_green g_m_top_25'>
                      <b>{approvedCount}</b>
                    </span>
                    <span className='g_tiny g_muted g_m_top_25'>
                      {pendingCount > 0 ? `${pendingCount} pending review` : 'All candidates processed'}
                    </span>
                  </div>
                </CardUI>

                <CardUI>
                  <div className='g_flex_v'>
                    <span className='g_muted g_small'>Rejected</span>
                    <span className='g_xxlarge g_m_top_25'>
                      <b>{rejectedCount}</b>
                    </span>
                    <span className='g_tiny g_muted g_m_top_25'>With rejection notes</span>
                  </div>
                </CardUI>
              </div>

              {/* Console & Pagination Bar */}
              <div className='pa_run_console_bar g_m_top_100'>
                <div className='g_flex_h' style={{ alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                  {runDate && (
                    <div className='g_flex_h' style={{ alignItems: 'center' }}>
                      <span className='g_small g_muted g_m_right_25'><b>Run Date:</b></span>
                      <span className='g_small'><b>{runDate}</b></span>
                    </div>
                  )}
                  <div className='g_flex_h' style={{ alignItems: 'center' }}>
                    <span className='g_small'>Page {currPage} of {totalPageCount}</span>
                  </div>
                  <div className='g_flex_h' style={{ alignItems: 'center' }}>
                    <span className='g_small g_muted'>
                      ({totalOffers != null ? totalOffers : safeList.length} offer{totalOffers === 1 ? '' : 's'} total
                      {filteredList.length !== safeList.length ? `, ${filteredList.length} shown` : ''})
                    </span>
                  </div>
                  {statusFilter && statusFilter !== 'ALL' && (
                    <BadgeUI label={`Filter: ${statusFilter}`} />
                  )}
                </div>

                {/* Top Pagination Controls */}
                <div className='g_flex_h pa_run_pagination_controls' style={{ alignItems: 'center', gap: '8px' }}>
                  {loadingPage && <SpinnerUI show={true} className='g_m_right_50' />}
                  <ButtonTinyUI
                    label='Previous'
                    title='Navigate to Older Check Run'
                    onClick={onPreviousPage}
                    disabled={isPreviousDisabled}
                    className='pa_btn_pagination'
                  />
                  <span className='g_small pa_page_indicator'>
                    Page {currPage} of {totalPageCount}
                  </span>
                  <ButtonTinyUI
                    label='Next'
                    title='Navigate to Newer Check Run'
                    onClick={onNextPage}
                    disabled={isNextDisabled}
                    className='pa_btn_pagination'
                  />
                </div>
              </div>

              {/* Eligibility Data Table */}
              {filteredList.length === 0 ? (
                <EmptyListUI
                  className='g_m_top_100'
                  context={context}
                  createDisabledMessageLabel={
                    searchQuery || (statusFilter && statusFilter !== 'ALL')
                      ? 'No repeat offers match your filter or search criteria'
                      : 'No repeat offers found'
                  }
                />
              ) : (
                <CardUI className='pa_table_card g_m_top_50'>
                  <table className='pa_table g_table_fixed'>
                    <thead className='g_small'>
                      <tr>
                        <th style={{ width: '14%', textAlign: 'left' }}>Account Name</th>
                        <th style={{ width: '14%', textAlign: 'left' }}>Current Outstanding Balance</th>
                        <th style={{ width: '13%', textAlign: 'left' }}>Months Since 1st disbursement</th>
                        <th style={{ width: '13%', textAlign: 'left' }}>Months Since last disbursement</th>
                        <th style={{ width: '13%', textAlign: 'left' }}>Eligible Amount</th>
                        <th style={{ width: '9%', textAlign: 'center' }}>Status</th>
                        <th style={{ width: '10%', textAlign: 'center' }}>Action</th>
                        <th style={{ width: '14%', textAlign: 'left' }}>Remark</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredList.map((item, index) => {
                        const accountName = getItemAccountName(item) || '-'
                        const accountId = getItemAccountId(item)
                        const exposure = item.existingExposureAmtF1 || item.existingExposureAmtF4 || item.existingExposureAmtF5 || item.existingExposureAmtF ||
                          (item.existingExposureAmt != null
                            ? (typeof item.existingExposureAmt === 'number' ? Currency.format(item.existingExposureAmt) : item.existingExposureAmt)
                            : (item.totalOutstandingBalance != null
                              ? (typeof item.totalOutstandingBalance === 'number' ? Currency.format(item.totalOutstandingBalance) : item.totalOutstandingBalance)
                              : (item.existingExposure != null
                                ? (typeof item.existingExposure === 'number' ? Currency.format(item.existingExposure) : item.existingExposure)
                                : (item.outstandingAmount ? Currency.format(item.outstandingAmount) : '-'))))

                        const firstDisbMonths = item.existingExposureMonths != null && item.existingExposureMonths !== ''
                          ? item.existingExposureMonths
                          : (item.monthsFromFirstDisbursement != null ? item.monthsFromFirstDisbursement : null)
                        const firstDisbursement = firstDisbMonths != null && firstDisbMonths !== '' ? `${firstDisbMonths} months` : '-'

                        const lastDisbMonths = item.lastLoanMonthElapsed != null && item.lastLoanMonthElapsed !== ''
                          ? item.lastLoanMonthElapsed
                          : (item.monthsFromLatestDisbursement != null ? item.monthsFromLatestDisbursement : null)
                        const lastDisbursement = lastDisbMonths != null && lastDisbMonths !== '' ? `${lastDisbMonths} months` : '-'

                        const eligibleAmount = getItemOfferAmount(item)
                        const displayOfferAmount = item.offerAmountF1 || item.offerAmountF5 || item.offerAmountF || (typeof eligibleAmount === 'number' ? Currency.format(eligibleAmount) : (eligibleAmount || '-'))
                        const normStatus = getNormalizedStatus(item)
                        const remark = item.remarks || item.remark || item.rejectionReason || '-'
                        const updatedDateText = getItemUpdatedDate(item)

                        return (
                          <tr key={item.id || index} className='g_small'>
                            <td className='g_text_top'>
                              {accountId ? (
                                <Link to={`/su/accounts/${accountId}/kyc`}>
                                  <b>{accountName}</b>
                                </Link>
                              ) : (
                                <b>{accountName}</b>
                              )}
                            </td>
                            <td className='g_text_top'>
                              {typeof exposure === 'number' ? Currency.format(exposure) : (exposure || '-')}
                            </td>
                            <td className='g_text_top'>
                              {firstDisbursement}
                            </td>
                            <td className='g_text_top'>
                              {lastDisbursement}
                            </td>
                            <td className='g_text_top g_col_green'>
                              <b>{displayOfferAmount}</b>
                            </td>
                            <td className='g_text_top g_text_center'>
                              <BadgeUI label={normStatus} />
                            </td>
                            <td className='g_text_top g_text_center'>
                              {normStatus === 'Pending' ? (
                                <ButtonTinyUI
                                  label='Approve/Reject'
                                  onClick={() => onOpenActionModal(item, 'approve')}
                                />
                              ) : normStatus === 'Approved' ? (
                                item.loanId ? (
                                  <Link to={`/su/loans/${item.loanId}/termSheet`} className='g_small'>
                                    <b>View Loan</b>
                                  </Link>
                                ) : (
                                  <span className='g_col_green'><b>Approved</b></span>
                                )
                              ) : (
                                <span className='g_muted'>-</span>
                              )}
                            </td>
                            <td className='g_text_top'>
                              <span className='g_tiny g_muted'>{remark}</span>
                              {updatedDateText && normStatus !== 'Pending' && (
                                <div className='g_tiny g_muted pa_date_updated'>
                                  Updated: {updatedDateText}
                                </div>
                              )}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </CardUI>
              )}
            </div>
          )}
        </div>
      )}

      {/* Action Modal */}
      <RepeatTransactionActionModalSF
        context={context}
        isOpen={actionModalOpen}
        item={selectedItem}
        initialActionType={actionModalType}
        onCancelClick={onCloseActionModal}
        onSuccess={onActionSuccess}
      />

      {/* Run Eligibility Check Confirmation Modal */}
      <ModalFormUI
        context={context}
        titleLabel='Run Repeat Loan Eligibility Check'
        buttonLabel='Confirm & Run'
        cancelLabel='Cancel'
        isOpen={eligibilityConfirmOpen}
        onRequestClose={onEligibilityConfirmModalRequestClose}
        errorList={errorList}
        onSubmit={onRunEligibilityCheckConfirm}
        busy={isCheckingEligibility}
      >
        <p className='g_m_bottom_50'>
          Are you sure you want to run the repeat loan eligibility check?
        </p>
        <div className='nach-modal-hint-box g_m_top_50'>
          <p className='g_small g_muted' style={{ margin: 0 }}>
            <b>Note:</b> This will re-evaluate eligible accounts and generate repeat loan offers based on repayment history and compliance.
          </p>
        </div>
      </ModalFormUI>
    </SuperUserHomePageUI>
  )
}

export default RepeatTransactionPageUI
